import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowRight, Copy, Check, Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import { apiGet } from "@/lib/api";
import {
  CATEGORIES,
  CATEGORY_ORDER,
  PRACTICES,
} from "@/lib/quizConfig";
import PersonalizedReport from "@/components/PersonalizedReport";
import { trackCustomOnce } from "@/lib/pixel";
import useNoIndex from "@/lib/useNoIndex";

const BUY_URL = "https://connexusdeck.com/quiz-connexus";
const PERSONAL_VIDEO_IDS = {
  foundation: "CPN8oKRw9Mc",
  quick_connect: "X_fz6MVMCcg",
  sensory_sync: "beF5Fn95bPY",
  grand_vision: "-u7G16e-bK0",
};
const ASSETS = {
  productMain:
    "https://customer-assets.emergentagent.com/job_connexus-quiz-flow/artifacts/hvabgwjt_Main%20Product%20Connexus.jpg",
  benefitsInfographic:
    "https://customer-assets.emergentagent.com/job_connexus-quiz-flow/artifacts/vj8sh4e9_03%20CC%20Cards_Amazon_images-12.jpg",
};

export default function Results() {
  useNoIndex();
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const res = await apiGet(`/quiz/results/${id}`);
        setData(res);
      } catch (e) {
        setError(
          e?.response?.data?.detail ||
            e?.message ||
            "Unable to load your results."
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-8 h-8 text-connexus animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white p-6 text-center">
        <p className="text-connexus-textSecondary mb-5">
          {error || "Results not found."}
        </p>
        <button
          onClick={() => navigate("/", { replace: true })}
          className="bg-connexus hover:bg-connexus-hover text-white px-6 py-3 rounded-full font-semibold"
        >
          Back to quiz
        </button>
      </div>
    );
  }

  const sorted = [...CATEGORY_ORDER].sort(
    (a, b) => data.scores[a] - data.scores[b]
  );
  const primary = sorted[0];
  const secondary = sorted[1];
  const primaryLabel = CATEGORIES[primary].title.toLowerCase();
  const secondaryLabel = CATEGORIES[secondary].title.toLowerCase();

  const scrollToOffer = () => {
    const el = document.getElementById("offer");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const goCheckout = () => {
    window.location.href = BUY_URL;
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      toast.success("Results link copied!");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Could not copy. Please copy manually.");
    }
  };

  return (
    <div className="min-h-screen bg-white pb-28">
      <div className="max-w-3xl mx-auto px-5 sm:px-6 pt-8 fade-up">
        <p className="text-[11px] sm:text-xs tracking-[0.25em] font-bold text-connexus mb-3">
          THE COUPLES CONNECTION QUIZ RESULTS
        </p>
        <h1
          className="font-heading text-4xl sm:text-5xl tracking-tight leading-[1.05] text-connexus mb-4"
          data-testid="results-headline"
        >
          Thank you, {data.first_name}.
        </h1>
        <p className="text-base sm:text-lg text-black leading-relaxed mb-3">
          Based on your answers, your relationship has strong potential — but{" "}
          {primaryLabel} and {secondaryLabel} are currently the biggest gaps.
        </p>

        {/* Personalized results video — conditional on PRIMARY category */}
        <div className="mb-6" data-testid="personal-video-block">
          <h2 className="font-heading text-xl sm:text-2xl tracking-tight text-connexus mb-3">
            A Personal Message From Wouter About Your Results
          </h2>
          <div className="w-full aspect-video rounded-2xl overflow-hidden bg-black shadow-connexus-card">
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${PERSONAL_VIDEO_IDS[primary]}?rel=0&modestbranding=1&playsinline=1`}
              title="A Personal Message From Wouter About Your Results"
              loading="lazy"
              allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
              className="w-full h-full border-0"
              data-testid="personal-video-iframe"
            />
          </div>
        </div>

        {/* CTA — under personalized video, scrolls to offer */}
        <div
          className="bg-connexus-accent rounded-2xl p-6 sm:p-7 mb-8 text-center"
          data-testid="video-cta-block"
        >
          <h3 className="font-heading text-xl sm:text-2xl tracking-tight text-connexus font-bold mb-2">
            Ready to take the next step?
          </h3>
          <p className="text-sm sm:text-base text-connexus-textSecondary leading-relaxed mb-5 max-w-md mx-auto">
            See how the Connexus Couples Card Deck can help you start practicing
            this tonight.
          </p>
          <button
            onClick={() => {
              trackCustomOnce("DeckClick", { location: "results-cta" });
              scrollToOffer();
            }}
            className="inline-flex items-center justify-center gap-2 bg-connexus hover:bg-connexus-hover text-white font-semibold px-7 py-3 rounded-full transition-colors shadow-connexus-soft active:scale-[0.98]"
            data-testid="video-cta-button"
          >
            See the Deck
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        <p className="text-sm sm:text-base text-connexus-textSecondary leading-relaxed mb-8">
          You&apos;ve taken a real step by being honest here. Below is your
          breakdown — and two simple practices you can start tonight.
        </p>

        {/* Breakdown */}
        <div className="bg-white border border-connexus-border rounded-3xl p-6 sm:p-8 shadow-connexus-card">
          <h2 className="font-heading text-xl sm:text-2xl text-connexus font-bold mb-5">
            Your Relationship Score Breakdown
          </h2>
          <div className="flex flex-col gap-5" data-testid="results-bar-chart">
            {CATEGORY_ORDER.map((cat) => {
              const s = data.scores[cat];
              const pct = Math.max(6, (s / 10) * 100);
              const isPrimary = cat === primary;
              const isSecondary = cat === secondary;
              const fillColor = isPrimary
                ? "bg-connexus"
                : isSecondary
                ? "bg-connexus-muted"
                : "bg-[#EBD8D3]";
              return (
                <div key={cat} data-testid={`score-bar-${cat}`}>
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="text-base">{CATEGORIES[cat].icon}</span>
                    <span className="flex-1 text-[15px] font-semibold text-black">
                      {CATEGORIES[cat].title}
                    </span>
                    {isPrimary && (
                      <span className="bg-connexus text-white text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full">
                        NEEDS LOVE
                      </span>
                    )}
                    {isSecondary && (
                      <span className="bg-connexus-accent text-connexus text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full border border-connexus-muted">
                        NEXT UP
                      </span>
                    )}
                    <span className="text-sm font-bold text-connexus-textSecondary">
                      {s.toFixed(1)}/10
                    </span>
                  </div>
                  <div className="h-3 bg-connexus-accent rounded-full overflow-hidden">
                    <div
                      className={`h-3 rounded-full transition-[width] duration-700 ease-out ${fillColor}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Private Link — moved below breakdown */}
        <div
          className="bg-connexus-accent rounded-2xl p-5 mt-8"
          data-testid="results-link-card"
        >
          <p className="text-[11px] tracking-[0.2em] font-bold text-connexus mb-1.5">
            YOUR PRIVATE RESULTS LINK
          </p>
          <p className="text-[13px] text-connexus-textSecondary leading-relaxed mb-3">
            Bookmark this page or copy the link below — come back anytime to
            revisit your results.
          </p>
          <button
            onClick={copyLink}
            className="w-full flex items-center gap-3 bg-white border border-connexus-muted rounded-full py-2.5 px-4 hover:border-connexus transition-colors"
            data-testid="copy-results-link"
          >
            <span className="flex-1 truncate text-[13px] font-semibold text-connexus text-left">
              {window.location.href}
            </span>
            <span className="flex items-center gap-1.5 text-[12px] font-bold tracking-wide text-connexus shrink-0">
              {copied ? (
                <>
                  <Check className="w-4 h-4" /> COPIED
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" /> COPY
                </>
              )}
            </span>
          </button>
        </div>

        {/* Personalized Relationship Report — accordion of all 4 categories */}
        <PersonalizedReport scores={data.scores} primary={primary} />

        {/* Bridge */}
        <div className="mt-12 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-connexus font-bold mb-3">
            Don&apos;t let this be a one-time thing.
          </h2>
          <p className="text-connexus-textSecondary leading-relaxed mb-5 max-w-xl mx-auto">
            The Connexus Couples Card Deck helps you go from &ldquo;we should do this
            more&rdquo; to actually feeling closer every week.
          </p>
          <button
            onClick={scrollToOffer}
            className="bg-connexus-accent hover:bg-connexus-accentDeep text-connexus font-semibold px-6 py-3 rounded-full transition-colors"
          >
            See the deck ↓
          </button>
        </div>

        {/* Primary focus */}
        <FocusCard
          kicker="YOUR PRIMARY FOCUS"
          category={primary}
          practiceLabel="A FIRST STEP YOU CAN TAKE TONIGHT"
          testId="practice-primary"
          variant="primary"
        />

        {/* Secondary focus */}
        <FocusCard
          kicker="NEXT OPPORTUNITY"
          category={secondary}
          practiceLabel="TRY THIS THIS WEEK"
          testId="practice-secondary"
          variant="secondary"
        />

        {/* Testimonials */}
        <div className="mt-12 text-center">
          <p className="text-[11px] tracking-[0.25em] font-bold text-connexus mb-4">
            WHAT COUPLES SAY
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <Testimonial
              name="Richard"
              body="We love the card deck, it has helped us both grow in partnership and commitment."
            />
            <Testimonial
              name="Jennifer"
              body="My husband and I got closer together in very tough times after being disconnected for a while."
            />
          </div>
        </div>

        {/* Offer */}
        <div
          id="offer"
          className="mt-12 bg-white border border-connexus-border rounded-3xl p-6 sm:p-8 shadow-connexus-card md:flex md:gap-8"
        >
          <div className="md:flex-1 mb-6 md:mb-0">
            <img
              src={ASSETS.productMain}
              alt="Connexus Couples Card Deck"
              className="w-full h-64 sm:h-80 object-contain rounded-2xl bg-white"
            />
          </div>
          <div className="md:flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="flex text-[#E6A700] gap-[2px]">
                <Star className="w-4 h-4 fill-current" />
                <Star className="w-4 h-4 fill-current" />
                <Star className="w-4 h-4 fill-current" />
                <Star className="w-4 h-4 fill-current" />
                <Star className="w-4 h-4 fill-current" />
              </span>
              <span className="text-[13px] font-semibold text-connexus-textSecondary">
                4.6 on Amazon
              </span>
            </div>
            <h3 className="font-heading text-2xl sm:text-3xl tracking-tight text-connexus font-bold mb-1">
              Connexus Couples Card Deck
            </h3>
            <p className="italic text-connexus-textSecondary mb-4 leading-relaxed">
              Deepen your connection. One card at a time.
            </p>

            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="text-base text-connexus-textMuted line-through">
                $34.99
              </span>
              <span className="text-3xl font-bold text-connexus">$29.99</span>
              <span className="bg-connexus-accent text-connexus text-[11px] font-bold tracking-wider px-2 py-0.5 rounded-full">
                SAVE 14%
              </span>
            </div>

            <div
              className="bg-connexus-accent border border-dashed border-connexus-muted rounded-2xl p-4 mb-5 flex flex-col items-center"
              data-testid="coupon-box"
            >
              <p className="text-[10px] tracking-[0.2em] font-bold text-connexus mb-2">
                USE COUPON AT CHECKOUT
              </p>
              <div className="inline-flex items-center gap-3 bg-white border border-connexus rounded-full py-2 px-4">
                <span
                  className="font-mono text-base font-extrabold tracking-widest text-connexus"
                  data-testid="coupon-code"
                >
                  CONNEXUS5OFF
                </span>
                <span className="bg-connexus text-white text-[11px] font-bold tracking-wider px-2 py-0.5 rounded-full">
                  $5 OFF
                </span>
              </div>
            </div>

            <ul className="flex flex-col gap-2.5 mb-6 text-sm leading-relaxed text-black">
              <li>🎁 Lifetime access to the Live Connexus Challenge for coaching &amp; support</li>
              <li>💬 40 powerful cards across 5 categories — from Foundation Builder to Sensory Sync</li>
              <li>🎥 Video guides with deeper insights &amp; examples</li>
              <li>💫 Perfect for date nights, cozy talks, or just having fun together</li>
            </ul>

            <button
              onClick={goCheckout}
              className="w-full inline-flex items-center justify-center gap-3 bg-connexus hover:bg-connexus-hover text-white rounded-full py-4 font-bold text-base shadow-connexus-soft transition-all active:scale-[0.98]"
              data-testid="buy-now-button"
            >
              Buy and Save $5
              <ArrowRight className="w-5 h-5" />
            </button>
            <p className="text-xs text-connexus-textMuted text-center mt-2.5">
              Ships in 2–4 business days · 30-day happiness guarantee
            </p>
          </div>
        </div>

        {/* Benefits infographic */}
        {/* More Than A Card Deck — value stack */}
        <div
          className="mt-12 bg-connexus-accent rounded-3xl p-7 sm:p-10 max-w-3xl mx-auto"
          data-testid="value-stack-section"
        >
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight text-connexus font-bold mb-3 text-left">
            More Than A Card Deck
          </h2>
          <p className="text-base sm:text-lg text-connexus-textSecondary leading-relaxed mb-8 text-left">
            When you order the Connexus Couples Card Deck today, you&apos;ll
            also receive complimentary VIP Access to our next Connexus
            Challenge.
          </p>

          {/* Item 1 — Card Deck */}
          <div className="bg-white border border-connexus-border rounded-2xl p-5 sm:p-6 mb-5">
            <h3 className="font-heading text-xl sm:text-2xl text-connexus font-bold mb-2 text-left">
              🎴 Connexus Couples Card Deck
            </h3>
            <p className="text-[15px] sm:text-base text-connexus-textSecondary leading-relaxed text-left">
              40 guided cards designed to help you create deeper communication,
              emotional connection, intimacy, and a shared vision for your
              future together.
            </p>
          </div>

          {/* Item 2 — VIP Challenge */}
          <div className="bg-white border border-connexus-border rounded-2xl p-5 sm:p-6 mb-6">
            <h3 className="font-heading text-xl sm:text-2xl text-connexus font-bold mb-2 text-left">
              ⚡ VIP Access To The Next Connexus Challenge
            </h3>
            <p className="text-[15px] sm:text-base text-connexus-textSecondary leading-relaxed mb-5 text-left">
              Join us live as we guide couples through the exact framework we
              use to rebuild connection, improve communication, and create
              relationships that feel alive again.
            </p>

            <p className="text-[11px] tracking-[0.2em] font-bold text-connexus mb-3 text-left">
              VIP INCLUDES
            </p>
            <ul className="flex flex-col gap-4 text-left">
              <li>
                <div className="flex items-start gap-3">
                  <span className="text-connexus text-lg leading-none mt-0.5">
                    ✅
                  </span>
                  <div>
                    <p className="font-semibold text-base text-black mb-1">
                      Lifetime Replay Access
                    </p>
                    <p className="text-[14px] sm:text-[15px] text-connexus-textSecondary leading-relaxed">
                      Revisit every lesson and training whenever you need it.
                    </p>
                  </div>
                </div>
              </li>
              <li>
                <div className="flex items-start gap-3">
                  <span className="text-connexus text-lg leading-none mt-0.5">
                    ✅
                  </span>
                  <div>
                    <p className="font-semibold text-base text-black mb-1">
                      VIP Zoom Q&amp;A
                    </p>
                    <p className="text-[14px] sm:text-[15px] text-connexus-textSecondary leading-relaxed">
                      Receive personalized guidance around your unique
                      relationship dynamics and challenges.
                    </p>
                  </div>
                </div>
              </li>
              <li>
                <div className="flex items-start gap-3">
                  <span className="text-connexus text-lg leading-none mt-0.5">
                    ✅
                  </span>
                  <div>
                    <p className="font-semibold text-base text-black mb-1">
                      10 Days of Personalized Coaching on Telegram
                    </p>
                    <p className="text-[14px] sm:text-[15px] text-connexus-textSecondary leading-relaxed">
                      Get direct support as you implement what you&apos;re
                      learning and create real change in your relationship.
                    </p>
                  </div>
                </div>
              </li>
            </ul>
          </div>

          {/* Bonus value highlight */}
          <div className="bg-white border-2 border-connexus rounded-2xl p-5 sm:p-6 text-left">
            <p className="text-[11px] tracking-[0.2em] font-bold text-connexus mb-1.5">
              TOTAL BONUS VALUE
            </p>
            <p className="font-heading text-3xl sm:text-4xl text-connexus font-bold mb-2">
              $341
            </p>
            <p className="text-[14px] sm:text-[15px] text-connexus-textSecondary leading-relaxed">
              Included free when you order your Connexus Couples Card Deck
              today.
            </p>
          </div>
        </div>

        {/* Final CTA */}
        <div className="mt-12 bg-connexus-accent rounded-3xl p-7 sm:p-9 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-connexus font-bold mb-2">
            Reconnect tonight.
          </h2>
          <p className="text-connexus-textSecondary leading-relaxed mb-5 max-w-md mx-auto">
            Small, honest conversations every day — that&apos;s how strong
            relationships are rebuilt.
          </p>
          <button
            onClick={goCheckout}
            className="w-full inline-flex items-center justify-center gap-3 bg-connexus hover:bg-connexus-hover text-white rounded-full py-4 font-bold text-base shadow-connexus-soft transition-all active:scale-[0.98]"
            data-testid="buy-now-button-2"
          >
            Buy and Save $5
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>

        <footer className="py-10 text-center">
          <p className="text-xs text-connexus-textMuted">
            © {new Date().getFullYear()} Connexus
          </p>
        </footer>
      </div>

      {/* Sticky mobile CTA */}
      <div
        className="fixed bottom-0 left-0 right-0 border-t border-connexus-border bg-white/95 backdrop-blur-md py-3 px-4 z-40"
        data-testid="mobile-sticky-cta"
      >
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-3">
          <div>
            <p className="text-base">
              <span className="text-xs text-connexus-textMuted line-through mr-2">
                $34.99
              </span>
              <span className="text-lg font-bold text-connexus">$29.99</span>
            </p>
            <p className="text-xs text-connexus-textSecondary">
              Free practices included
            </p>
          </div>
          <button
            onClick={goCheckout}
            className="bg-connexus hover:bg-connexus-hover text-white font-bold text-sm py-3 px-5 rounded-full transition-colors"
            data-testid="sticky-buy-now-button"
          >
            Buy &amp; Save $5
          </button>
        </div>
      </div>
    </div>
  );
}

function FocusCard({ kicker, category, practiceLabel, testId, variant = "primary" }) {
  const cat = CATEGORIES[category];
  const practice = PRACTICES[category];
  const isPrimary = variant === "primary";

  return (
    <div
      className={`mt-8 bg-white rounded-3xl overflow-hidden border ${
        isPrimary
          ? "border-connexus-borderStrong shadow-connexus-soft"
          : "border-connexus-border shadow-connexus-card"
      }`}
    >
      {/* Magenta header strip */}
      <div
        className={`bg-connexus ${isPrimary ? "py-2.5 px-5 sm:px-6" : "py-2 px-5 sm:px-6"}`}
      >
        <p className={`tracking-[0.25em] font-bold text-white ${isPrimary ? "text-[11px] sm:text-xs" : "text-[10px] sm:text-[11px]"}`}>
          {kicker}
        </p>
      </div>

      {/* Card body */}
      <div className={`${isPrimary ? "p-6 sm:p-8" : "p-5 sm:p-7"}`}>
        <h3
          className={`font-heading text-connexus font-bold ${
            isPrimary ? "text-2xl sm:text-3xl mb-2" : "text-xl sm:text-2xl mb-1.5"
          }`}
        >
          {cat.icon} {cat.title}
        </h3>
        <p
          className={`text-connexus-textSecondary leading-relaxed ${
            isPrimary ? "text-base mb-6" : "text-[15px] mb-5"
          }`}
        >
          {cat.description}
        </p>

        <div
          className={`rounded-2xl border ${
            isPrimary
              ? "bg-connexus-accent border-connexus-muted p-5 sm:p-6"
              : "bg-connexus-surface border-connexus-border p-4 sm:p-5"
          }`}
          data-testid={testId}
        >
          <p className="text-[10px] tracking-[0.2em] font-bold text-connexus mb-1.5">
            {practiceLabel}
          </p>
          <h4
            className={`font-bold text-black mb-2 ${
              isPrimary ? "text-lg" : "text-base"
            }`}
          >
            {practice.title}
          </h4>
          <p
            className={`text-connexus-textSecondary leading-relaxed ${
              isPrimary ? "text-[15px]" : "text-sm"
            }`}
          >
            {practice.body}
          </p>
        </div>
      </div>
    </div>
  );
}

function Testimonial({ name, body }) {
  return (
    <div className="bg-white border border-connexus-border rounded-2xl p-5 text-left">
      <p className="text-[#E6A700] tracking-wider mb-2">★★★★★</p>
      <p className="italic text-black leading-relaxed mb-2">
        &ldquo;{body}&rdquo;
      </p>
      <p className="text-connexus-textSecondary font-semibold">— {name}</p>
    </div>
  );
}
